/*!
 * RTL Fix + Hebrew Font + Payment Icons
 * תחליף חינמי לפלאגין RTL בתשלום
 * for Shopify (Dawn theme)
 *
 * Copyright (c) 2026 Artur Gaibishel
 * Released under the MIT License — see the LICENSE file for details.
 *
 * Optional config — set BEFORE this script loads:
 *   <script>window.RTLFixConfig = { font: 'Rubik', paymentIcons: true };</script>
 */
(function (window, document) {
  'use strict';

  var NS = 'rtlfix';

  /* Loaded twice (theme + app embed)? Do nothing the second time. */
  if (document.getElementById(NS + '-css')) return;

  // ============================================================
  // CONFIG
  // ============================================================
  var userConfig = window.RTLFixConfig || {};
  var config = {
    font: userConfig.font || 'Rubik',
    loadFont: userConfig.loadFont !== false,
    paymentIcons: userConfig.paymentIcons !== false,
    courseArrows: userConfig.courseArrows !== false,
    iframePatch: userConfig.iframePatch !== false
  };

  var FONT = '"' + config.font + '", "Noto Sans Hebrew", Arial, sans-serif';

  // ============================================================
  // CSS — RTL + פונט עברי
  // ============================================================
  var STYLES = `

  /* ── Base ─────────────────────────────────────────────────
     direction only — we never force a global text-align, that
     breaks every deliberately centred section in the theme. */
  html { direction: rtl !important; }
  body {
    direction: rtl !important;
    font-family: ${FONT} !important;
  }
  p, h1, h2, h3, h4, h5, h6, li, td, th,
  label, input, textarea, select, button {
    font-family: ${FONT} !important;
  }

  /* ── Form fields ──────────────────────────────────────────
     Hebrew fields get RTL; Latin-only fields must stay LTR or
     the punctuation reorders (a@b.com renders as com.b@a). */
  input, textarea, select {
    direction: rtl !important;
    font-family: ${FONT} !important;
  }
  input[type="email"],
  input[type="tel"],
  input[type="url"],
  input[type="password"],
  input[type="number"],
  input[inputmode="email"],
  input[inputmode="tel"],
  input[inputmode="numeric"],
  input[inputmode="decimal"],
  input[name*="card"],
  input[autocomplete*="cc-"] {
    direction: ltr !important;
    text-align: right !important;
  }

  /* Text starts on the right, floating label follows it */
  .field__input, .customer .field input {
    text-align: right !important;
  }
  .field__label, .customer .field label,
  .field__input:focus ~ .field__label,
  .field__input:not(:placeholder-shown) ~ .field__label {
    left: auto !important;
    right: calc(var(--inputs-border-width) + 2rem) !important;
  }

  /* ── Prices ───────────────────────────────────────────────
     Isolate the bidi run so ₪, digits, ranges and discount
     percentages keep their own order inside RTL text. */
  .price, .price-item, .price__container,
  .cart-item__price-wrapper, .totals__total-value,
  .unit-price, .product__price {
    unicode-bidi: isolate;
  }

  /* Sale badge sits next to the price — give it breathing room */
  .price .price__badge-sale, .price .price__badge-sold-out {
    margin-right: 1rem !important;
    margin-left: 0 !important;
  }

  /* ── Sliders ──────────────────────────────────────────────
     Dawn's own carousels stay RTL: their JS computes a negative
     offset in RTL, so next/prev work and the first image lands
     on the right. LTR is only for third-party libraries that
     don't understand RTL, plus the main slideshow. */
  .slideshow, .swiper, .swiper-wrapper, .flickity-viewport {
    direction: ltr !important;
  }
  /* ...but the Hebrew copy inside the slideshow must stay RTL */
  .slideshow__text, .slideshow__text-wrapper,
  .banner__box, .slideshow .slider-counter {
    direction: rtl !important;
  }

  /* Nav arrows — mirror the icon so next points left, prev right */
  .slider-button--next .icon { transform: rotate(90deg) !important; }
  .slider-button--prev .icon { transform: rotate(-90deg) !important; }
  /* The slideshow stayed LTR — restore its arrows */
  .slideshow .slider-button--next .icon { transform: rotate(-90deg) !important; }
  .slideshow .slider-button--prev .icon { transform: rotate(90deg) !important; }

  /* Product grid — always RTL (wins over .slider if both apply,
     because this rule comes later in the sheet) */
  .product-grid { direction: rtl !important; }

  /* ── Product media gallery ────────────────────────────────
     Dawn bleeds the media list past the page gutter using a
     negative margin-left plus padding-left on the first slide.
     Those are physical properties: under direction:rtl they
     land on the wrong edge, so the first image sits off-centre
     with a sliver of the next slide showing. Neutralise the
     bleed and let each slide fill the track exactly. */
  @media screen and (max-width: 749px) {
    .product__media-list {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 100% !important;
      scroll-padding-left: 0 !important;
      scroll-padding-right: 0 !important;
    }
    .product__media-list .product__media-item,
    .product__media-list .product__media-item:first-child,
    .product__media-list .product__media-item:last-child {
      padding-left: 0 !important;
      padding-right: 0 !important;
      width: 100% !important;
    }
    /* No side padding on mobile — the desktop rule below used to
       apply here too and pushed the image off-centre. */
    .product__media-wrapper, .product__info-wrapper {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    /* Thumbnails sit in a grid track that is wider than the row —
       centre them instead of letting them hug the start edge. */
    .product__media-wrapper .thumbnail-list {
      justify-content: center !important;
      justify-items: center !important;
    }
  }

  /* Product page — spacing between image and text (desktop only) */
  @media screen and (min-width: 750px) {
    .product__info-wrapper {
      padding-right: 2rem !important;
      padding-left: 1rem !important;
    }
    .product__media-wrapper {
      padding-left: 2rem !important;
      padding-right: 1rem !important;
    }
    .product .grid--gapless { gap: 2rem !important; }
  }

  /* ── Mobile menu drawer — opens from the RIGHT ─────────────
     Two rules on purpose: the CLOSED state hides to the right,
     the OPEN state (higher specificity) keeps translate(0) so
     the menu still opens. Both !important so neither wins by
     source order — specificity decides, and open always wins. */
  .menu-drawer {
    left: auto !important;
    right: 0 !important;
    transform: translate(100%) !important;
  }
  .js .menu-drawer__submenu { transform: translate(-100%) !important; }
  .js details[open].menu-opening > .menu-drawer,
  .js details[open].menu-opening > .menu-drawer__submenu {
    transform: translate(0) !important;
  }

  /* Submenu arrow → left side, with room on the left for it */
  .menu-drawer summary.menu-drawer__menu-item {
    padding-right: 3rem !important;
    padding-left: 5.2rem !important;
  }
  .menu-drawer__menu-item > .icon-arrow {
    right: auto !important;
    left: 3rem !important;
    transform: translateY(-50%) rotate(180deg) !important;
  }

  /* Back button arrow → point the other way for RTL */
  .menu-drawer__close-button .icon-arrow {
    transform: none !important;
    margin-right: 0 !important;
    margin-left: 1rem !important;
  }

  /* ── Systematic physical-property flips ───────────────────
     Dawn positions these with left/right, which do not mirror
     under direction:rtl. Flip them to the correct side. */

  /* Accordion / collapsible caret (FAQ, product tabs) */
  summary .icon-caret {
    right: auto !important;
    left: 1.5rem !important;
  }

  /* Header dropdown caret — flow inline AFTER the text (lands on
     the left in RTL) instead of absolute, so it never overlaps */
  .header__menu-item .icon-caret {
    position: static !important;
    margin: 0 .4rem 0 0 !important;
    vertical-align: middle;
  }

  /* Select / dropdown caret (sort by, country, etc.) */
  .select .icon-caret, .customer select + svg {
    right: auto !important;
    left: calc(var(--inputs-border-width) + 1.5rem) !important;
  }

  /* Card badge (Sale / Sold out on collection cards) */
  .card__badge {
    left: auto !important;
    right: 1.3rem !important;
  }

  /* Modal / popup close buttons */
  .modal__close-button,
  .product-popup-modal__toggle {
    right: auto !important;
    left: 1rem !important;
  }

  /* Language / currency disclosure dropdown */
  .disclosure__list {
    right: auto !important;
    left: 0 !important;
  }

  /* Rich-text lists and quotes — Dawn indents with padding-left,
     which leaves the indent on the wrong side in RTL */
  .rte ul, .rte ol {
    padding-left: 0 !important;
    padding-right: 4rem !important;
  }
  .rte blockquote {
    border-left: none !important;
    border-right: 0.2rem solid rgba(var(--color-foreground), 0.2) !important;
    padding-left: 0 !important;
    padding-right: 1.5rem !important;
  }

  /* Breadcrumbs / cart / pagination */
  .breadcrumb, .breadcrumbs { direction: rtl; }
  .cart__item, .cart-item { direction: rtl; }
  .pagination { direction: rtl; }

  /* ── Buttons — centre the label ───────────────────────────── */
  .product-form__submit,
  button[name="add"],
  .shopify-payment-button__button,
  .shopify-payment-button button,
  [class*="payment-button"],
  [class*="checkout-button"],
  button.product-form__submit {
    text-align: center !important;
    justify-content: center !important;
  }

  /* ── Footer ───────────────────────────────────────────────
     Scoped to .footer on purpose — an unscoped [class*="brand"]
     or [class*="newsletter"] hits unrelated third-party apps. */
  .footer__brand,
  .footer [class*="brand-information"],
  .footer [class*="brand_information"] {
    overflow: visible !important;
    text-align: right !important;
    word-break: break-word !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .footer__blocks, .footer__inner, .footer-block__details {
    overflow: visible !important;
    word-break: break-word !important;
  }
  .footer-block__newsletter,
  .footer [class*="newsletter"] {
    text-align: right !important;
    direction: rtl !important;
  }
  .footer-block__newsletter p,
  .footer-block__newsletter h2,
  .footer-block__newsletter label,
  .footer-block__newsletter span {
    text-align: right !important;
  }
  /* The email field itself stays LTR — see the input rules above */
  .footer-block__newsletter input[type="email"] {
    direction: ltr !important;
    text-align: right !important;
  }

  /* ── Payment icons ────────────────────────────────────────── */
  #${NS}-payment-icons {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 0 8px;
    direction: ltr;
  }
  .${NS}-pay-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    width: 52px;
    height: 32px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
  .${NS}-pay-icon svg { width: 100%; height: 100%; }
  `;

  // ============================================================
  // PAYMENT ICONS SVG
  // ============================================================
  function icon(label, body) {
    return '<span class="' + NS + '-pay-icon" role="img" aria-label="' + label + '">' +
      '<svg viewBox="0 0 52 32" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">' +
      body +
      '</svg></span>';
  }

  var PAYMENT_ICONS = [

    icon('Visa',
      '<rect width="52" height="32" rx="4" fill="#1A1F71"/>' +
      '<text x="26" y="22" font-family="Arial,sans-serif" font-size="13" font-weight="bold" fill="white" text-anchor="middle" letter-spacing="1">VISA</text>'),

    icon('Mastercard',
      '<rect width="52" height="32" rx="4" fill="#252525"/>' +
      '<circle cx="20" cy="16" r="9" fill="#EB001B"/>' +
      '<circle cx="32" cy="16" r="9" fill="#F79E1B"/>' +
      '<path d="M26 8.8a9 9 0 0 1 0 14.4A9 9 0 0 1 26 8.8z" fill="#FF5F00"/>'),

    icon('Apple Pay',
      '<rect width="52" height="32" rx="4" fill="#000"/>' +
      '<path d="M19.5 9.5c.6-.7 1-1.6.9-2.5-.9.1-2 .6-2.6 1.3-.6.6-1.1 1.6-1 2.5 1 0 2-.5 2.7-1.3z" fill="white"/>' +
      '<path d="M20.4 11c-1.5-.1-2.7.8-3.4.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.3 1.1 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.2-2.6-.1 0-2.4-.9-2.4-3.6 0-2.3 1.8-3.3 1.9-3.4-.9-1.4-2.4-1.7-2.4-1.7z" fill="white"/>' +
      '<text x="36" y="20" font-family="Arial,sans-serif" font-size="8" font-weight="600" fill="white" text-anchor="middle">Pay</text>'),

    icon('Google Pay',
      '<rect width="52" height="32" rx="4" fill="white" stroke="#e0e0e0" stroke-width="1"/>' +
      '<text x="10" y="21" font-family="Arial,sans-serif" font-size="11" font-weight="bold" fill="#4285F4">G</text>' +
      '<text x="18" y="21" font-family="Arial,sans-serif" font-size="9" font-weight="500" fill="#5F6368">Pay</text>'),

    icon('Bit',
      '<rect width="52" height="32" rx="4" fill="#0E4C96"/>' +
      '<text x="26" y="21" font-family="Arial,sans-serif" font-size="13" font-weight="bold" fill="white" text-anchor="middle">bit</text>')

  ].join('');

  // ============================================================
  // HELPERS
  // ============================================================
  function debounce(fn, wait) {
    var timer = null;
    return function () {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        fn();
      }, wait);
    };
  }

  // ============================================================
  // HEBREW FONT
  // ============================================================
  function loadFont() {
    if (!config.loadFont) return;

    var family = config.font.replace(/ /g, '+');

    /* Already linked in theme.liquid (the FOUC-free way)? Skip. */
    if (document.querySelector('link[href*="' + family + '"]')) return;

    var preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://fonts.gstatic.com';
    preconnect.crossOrigin = 'anonymous';

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + family +
      ':wght@300;400;500;600;700&display=swap';

    document.head.appendChild(preconnect);
    document.head.appendChild(link);
  }

  // ============================================================
  // PAYMENT ICONS
  // ============================================================
  function injectPaymentIcons() {
    if (!config.paymentIcons) return;
    if (document.getElementById(NS + '-payment-icons')) return;

    var selectors = [
      '.footer__content-bottom',
      '.footer__content',
      '.footer',
      'footer',
      '.site-footer',
      '#shopify-section-footer'
    ];

    var footer = null;
    for (var i = 0; i < selectors.length; i++) {
      footer = document.querySelector(selectors[i]);
      if (footer) break;
    }
    if (!footer) return;

    var container = document.createElement('div');
    container.id = NS + '-payment-icons';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', 'אמצעי תשלום מקובלים');
    container.innerHTML = PAYMENT_ICONS;
    footer.appendChild(container);
  }

  // ============================================================
  // DYNAMIC BUTTONS
  // ============================================================
  var BUTTON_SELECTORS = [
    '.product-form__submit',
    'button[name="add"]',
    '.shopify-payment-button__button',
    '[class*="payment-button"] button',
    '[class*="checkout-button"]'
  ].join(',');

  function fixButtons() {
    document.querySelectorAll(BUTTON_SELECTORS).forEach(function (el) {
      if (el.dataset[NS + 'Btn']) return;
      el.style.setProperty('text-align', 'center', 'important');
      el.style.setProperty('justify-content', 'center', 'important');
      el.dataset[NS + 'Btn'] = '1';
    });
  }

  /* Same-origin Shopify iframes get the style once each. Marking
     cross-origin frames too, so we stop retrying them on every
     DOM mutation. */
  function patchIframes() {
    if (!config.iframePatch) return;

    document.querySelectorAll('iframe').forEach(function (frame) {
      if (frame.dataset[NS + 'Frame']) return;
      frame.dataset[NS + 'Frame'] = '1';
      try {
        var doc = frame.contentDocument || frame.contentWindow.document;
        if (!doc || !doc.head) {
          delete frame.dataset[NS + 'Frame'];
          return;
        }
        var style = doc.createElement('style');
        style.textContent = 'button,a{text-align:center!important;justify-content:center!important;}';
        doc.head.appendChild(style);
      } catch (e) {
        /* cross-origin — nothing we can do, and never retry */
      }
    });
  }

  /* LTD Courses — swap the arrow-left icon for arrow-down.
     The app injects the class dynamically, so this reruns
     from the observer as well. */
  function fixCourseArrows() {
    if (!config.courseArrows) return;
    document.querySelectorAll('.ldt-cr-arrow-left').forEach(function (el) {
      el.classList.remove('ldt-cr-arrow-left');
      el.classList.add('ldt-cr-arrow-down');
    });
  }

  // ============================================================
  // INIT
  // ============================================================
  function run() {
    fixButtons();
    patchIframes();
    fixCourseArrows();
  }

  function init() {
    loadFont();

    var style = document.createElement('style');
    style.id = NS + '-css';
    style.textContent = STYLES;
    document.head.appendChild(style);

    injectPaymentIcons();
    run();

    window.addEventListener('load', function () {
      injectPaymentIcons();
      run();
    });

    /* Watch for dynamically added elements (payment buttons,
       cart drawer, app widgets). Debounced — this used to fire
       on every single mutation. */
    var observer = new MutationObserver(debounce(run, 150));
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}(window, document));
